import React, { useContext, useMemo } from 'react';
import './Cart.css';
import { LocaleContext, CartContext } from '../locale';

export default function Cart() {
  const { locale } = useContext(LocaleContext);
  const { cart, addToCart, removeFromCart } = useContext(CartContext);

  const nf = useMemo(
    () => new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' }),
    [locale]
  );

  const subtotal = (Array.isArray(cart) ? cart : []).reduce(
    (acc, it) => acc + Number(it.price || 0) * Number(it.quantity || 1),
    0
  );

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  // Set exact quantity, compatible with carts that add by 1 per call
  const setQuantity = (item, qty) => {
    const desired = clamp(Number(qty || 1), 0, Number(item.stock_quantity || 99) || 99);
    if (desired <= 0) {
      removeFromCart(item.id);
      return;
    }
    // Clear then re-add desired quantity
    removeFromCart(item.id);
    addToCart({ ...item, quantity: desired }, desired);
    for (let i = 1; i < desired; i++) addToCart(item);
  };

  const inc = (item) => {
    const current = Number(item.quantity || 1);
    const max = Number(item.stock_quantity || 99) || 99;
    if (current >= max) return;
    // simple increment
    addToCart(item);
  };

  const dec = (item) => {
    const current = Number(item.quantity || 1);
    if (current <= 1) {
      removeFromCart(item.id);
    } else {
      setQuantity(item, current - 1);
    }
  };

  const clearCart = () => {
    (Array.isArray(cart) ? cart : []).forEach((it) => removeFromCart(it.id));
  };

  if (!cart || cart.length === 0) {
    return (
      <section className="cart-page">
        <div className="container">
          <div className="page-header">
            <div className="header-content">
              <div className="header-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="m1 1 4 4 2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
              </div>
              <div className="header-text">
                <h1 className="h1">Votre panier</h1>
                <p className="page-subtitle">Votre panier est vide</p>
              </div>
            </div>
          </div>
          <div className="cart-empty">
            <div className="empty-emoji">🛒</div>
            <h3>Votre panier est vide</h3>
            <p>Découvrez nos produits et ajoutez vos favoris.</p>
            <a href="#/products" className="btn primary">Voir les produits</a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="cart-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="m1 1 4 4 2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </div>
            <div className="header-text">
              <h1 className="h1">Votre panier</h1>
              <p className="page-subtitle">{cart.length} article(s)</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn secondary" onClick={clearCart} title="Vider le panier">
              Vider le panier
            </button>
            <a className="btn" href="#/products">Continuer mes achats</a>
          </div>
        </div>

        <div className="cart-layout">
          <div className="cart-list">
            {cart.map((item) => {
              const emoji = item.category === 'beer' ? '🍺' : item.category === 'wine' ? '🍷' : item.category === 'whisky' ? '🥃' : '🧀';
              const lineTotal = Number(item.price || 0) * Number(item.quantity || 1);
              const out = Number(item.stock_quantity || 0) <= 0;
              return (
                <div key={item.id} className="cart-item">
                  <div className={`ci-media ${item.category}`}>
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className="ci-fallback" style={{ display: item.image_url ? 'none' : 'flex' }}>{emoji}</div>
                  </div>

                  <div className="ci-body">
                    <div className="ci-head">
                      <div className="ci-title">{item.name}</div>
                      <button className="ci-remove" title="Retirer" onClick={() => removeFromCart(item.id)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                    <div className="ci-meta">
                      <span className="brand">{item.marque}</span>
                      {item.type ? <><span className="sep">•</span><span>{item.type}</span></> : null}
                      {item.volume ? <><span className="sep">•</span><span>{item.volume}</span></> : null}
                      {item.abv ? <><span className="sep">•</span><span>{item.abv}% vol</span></> : null}
                    </div>

                    <div className="ci-row">
                      <div className="ci-qty">
                        <button onClick={() => dec(item)} aria-label="Diminuer">−</button>
                        <div className="qty-value">{item.quantity || 1}</div>
                        <button onClick={() => inc(item)} aria-label="Augmenter" disabled={out}>+</button>
                      </div>
                      <div className="ci-price">{nf.format(item.price)}</div>
                      <div className="ci-total">{nf.format(lineTotal)}</div>
                    </div>

                    {out ? <div className="ci-alert">Rupture de stock</div> : null}
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="cart-summary">
            <div className="summary-card">
              <div className="sum-row">
                <span>Sous-total</span>
                <strong>{nf.format(subtotal)}</strong>
              </div>
              <div className="sum-note">Les frais de livraison et taxes sont calculés à l’étape suivante.</div>
              <a href="#/checkout" className="btn primary sum-btn">
                Passer au paiement
              </a>
              <a className="btn secondary sum-continue" href="#/products">Continuer mes achats</a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
